import { useCallback, useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { analyticsV2Service } from '@/services/analyticsV2.service';
import { queryClient } from '@/lib/query-client';
import {
  SENTINEL_METRIC_KEYS,
  SENTINEL_SERIES_KEYS,
  type SentinelAnalyticsQuery,
  type SentinelBreakdownRow,
  type SentinelDimension,
  type SentinelMetricKey,
} from '@/modules/sentinel-analytics/metric-keys';
import { toSentinelReport } from '@/modules/sentinel-analytics/report';
import type { SentinelAnalyticsReport } from '@/types/sentinelAnalytics';
import type {
  AnalyticsV2QueryParams,
  CategoryTableRow,
  CostBreakdown,
  CostBreakdownGroupBy,
  CostFeatures,
  CostQueryParams,
  CostTicketTypes,
  CostGranularity,
  CostSeries,
  CostSeriesGroupBy,
  CostSummary,
  CostTicketDetail,
  CostTickets,
  CostViewerList,
  UsagePriceInput,
  UsagePriceList,
  CostWeekly,
  CostWeeklyLevel,
  CoverageFunnelResult,
  Granularity,
  ScalarMetricKey,
  ScalarMetricResult,
  ScalarMetricsBatch,
  SeriesMetricKey,
  SeriesMetricsBatch,
  TicketCategoryBucket,
} from '@/types/analyticsV2';

const METRIC_STALE_TIME = 60 * 1000;

interface QueryState<T> {
  data: T | undefined;
  isError: boolean;
  isFetching: boolean;
}

export interface AnalyticsQueryResult<T> extends QueryState<T> {
  refetch: () => void;
}

/** Every analytics read shares this key prefix, so one invalidation reaches them all. */
const analyticsKey = (key: readonly unknown[]) => ['analytics-v2', ...key];

/**
 * The one way an analytics read reaches a page: fetches through the shared query cache whenever
 * `queryKey` changes (`null` waits), and starts from the cache so a remounted view shows its data at once.
 * Built on `queryClient.fetchQuery` because only `@tanstack/query-core` is installed (no React adapter).
 */
export function useAnalyticsQuery<T>(queryKey: readonly unknown[] | null, fetch: () => Promise<T>): AnalyticsQueryResult<T> {
  const [state, setState] = useState<QueryState<T>>(() => ({
    data: queryKey ? queryClient.getQueryData<T>(analyticsKey(queryKey)) : undefined,
    isError: false,
    isFetching: false,
  }));
  const [attempt, setAttempt] = useState(0);
  const refetch = useCallback(() => setAttempt((n) => n + 1), []);
  const key = queryKey === null ? null : JSON.stringify(queryKey);

  useEffect(() => {
    if (key === null) return;
    let cancelled = false;
    setState((s) => ({ ...s, isFetching: true, isError: false }));
    queryClient
      .fetchQuery({ queryKey: analyticsKey(queryKey!), queryFn: fetch, staleTime: METRIC_STALE_TIME })
      .then((data) => !cancelled && setState({ data, isError: false, isFetching: false }))
      .catch(() => !cancelled && setState((s) => ({ ...s, isError: true, isFetching: false })));
    return () => {
      cancelled = true;
    };
    // `queryKey` and `fetch` are rebuilt every render; `key` is their content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attempt]);

  return { ...state, refetch };
}

/** The `{ data, isLoading, isError }` shape the analytics-v2 sections read. */
export interface LoadState<T> {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
}

/** A failed read shows no data, so a section never keeps the previous range's numbers under an error. */
const loadState = <T>({ data, isFetching, isError }: AnalyticsQueryResult<T>): LoadState<T> => ({
  data: isError ? undefined : data,
  isLoading: isFetching,
  isError,
});

/**
 * Per-key scalar metric fetcher — each key resolves independently through the shared cache.
 */
export interface PerKeyMetricResult extends LoadState<ScalarMetricResult> {
  key: ScalarMetricKey;
}

/**
 * The brand belongs in the cache KEY, not only in the request. Switching brands does not
 * clear the cache, so without it the second brand is served the first brand's numbers.
 * `'all'` rather than the bare null so an unscoped read cannot collide with a brand id.
 */
export function useCategories(
  fromDate: string,
  toDate: string,
  accountId: number,
  ticketCategory?: TicketCategoryBucket,
  // Last on purpose: `TicketCategoryBucket` is a string union and would be assignable to a
  // `string` parameter, so a caller passing the category positionally into a `buId` slot
  // ahead of it would type-check and silently scope the request to a brand named "CS".
  buId?: string | null,
): LoadState<CategoryTableRow[]> {
  return loadState(
    useAnalyticsQuery(
      fromDate && toDate ? ['categories', accountId, fromDate, toDate, ticketCategory ?? null, buId ?? 'all'] : null,
      () => analyticsV2Service.getCategories({ fromDate, toDate, ticketCategory, accountId, buId }),
    ),
  );
}

/** Coverage Funnel for the super-admin Internal Analytics page. */
export function useFunnel(
  fromDate: string,
  toDate: string,
  accountId: number,
  buId?: string | null,
): LoadState<CoverageFunnelResult> {
  return loadState(
    useAnalyticsQuery(fromDate && toDate ? ['funnel', accountId, fromDate, toDate, buId ?? 'all'] : null, () =>
      analyticsV2Service.getFunnel({ fromDate, toDate, accountId, buId }),
    ),
  );
}

/** Keyed like `use-consolidated-metrics`' `perKeyKey`, which seeds this cache from the accounts list. */
function useSingleMetric(
  key: ScalarMetricKey,
  fromDate: string,
  toDate: string,
  accountId: number,
  buId?: string | null,
): PerKeyMetricResult {
  const query = useAnalyticsQuery<ScalarMetricsBatch>(
    fromDate && toDate ? ['metric', accountId, fromDate, toDate, key, buId ?? 'all'] : null,
    () => analyticsV2Service.getMetrics([key], { fromDate, toDate, accountId, buId }),
  );
  return { key, ...loadState(query), data: query.data?.[key] };
}

/**
 * Fan out one request per key. Callers pass a stable `keys` array (typically
 * the module-level `SCALAR_METRIC_KEYS` constant) — the hook calls
 * `useSingleMetric` once per index. The array length must be stable across
 * renders (it is — `SCALAR_METRIC_KEYS` is a frozen const).
 */
export function useMetricsPerKey(
  keys: ScalarMetricKey[],
  fromDate: string,
  toDate: string,
  accountId: number,
  buId?: string | null,
): PerKeyMetricResult[] {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return keys.map((key) => useSingleMetric(key, fromDate, toDate, accountId, buId));
}


// ─── Series ───────────────────────────────────────────────────────────────────
// Fetched separately from scalar because it has its own granularity dimension. Each caller has
// its own key, so two charts asking for different series never overwrite each other.

export function useMetricSeries(
  keys: SeriesMetricKey[],
  fromDate: string,
  toDate: string,
  granularity: Granularity,
  accountId: number,
  buId?: string | null,
): LoadState<SeriesMetricsBatch> {
  const sortedKeys = [...keys].sort().join(',');
  return loadState(
    useAnalyticsQuery(
      fromDate && toDate && granularity && keys.length > 0
        ? ['series', accountId, fromDate, toDate, granularity, sortedKeys, buId ?? 'all']
        : null,
      () => analyticsV2Service.getSeries(keys, { fromDate, toDate, granularity, accountId, buId }),
    ),
  );
}


// ─── AI cost ledger ────────────────────────────────────────────────────────────
// The query key carries every input (brand included, as 'all' when unscoped) so a switch never serves stale rows.

export type CostQueryResult<T> = LoadState<T>;

function useCostQuery<T>(
  queryKey: readonly unknown[],
  queryFn: () => Promise<T>,
  enabled: boolean,
): CostQueryResult<T> {
  return loadState(useAnalyticsQuery(enabled ? ['cost', ...queryKey] : null, queryFn));
}

const rangeKey = (p: AnalyticsV2QueryParams) => [p.accountId, p.fromDate, p.toDate, p.buId ?? 'all'];
/** Range key plus the ticket filters, so a filter change never serves another filter's rows. */
const costKey = (p: CostQueryParams) => [...rangeKey(p), p.channel ?? 'all', p.category ?? 'all', p.intent ?? 'all'];
const hasRange = (p: AnalyticsV2QueryParams) => !!(p.fromDate && p.toDate);

export const useCostSummary = (p: CostQueryParams): CostQueryResult<CostSummary> =>
  useCostQuery(['summary', ...costKey(p)], () => analyticsV2Service.getCostSummary(p), hasRange(p));

export const useCostSeries = (
  p: CostQueryParams,
  granularity: CostGranularity,
  groupBy: CostSeriesGroupBy,
): CostQueryResult<CostSeries> =>
  useCostQuery(
    ['series', ...costKey(p), granularity, groupBy],
    () => analyticsV2Service.getCostSeries(p, granularity, groupBy),
    hasRange(p),
  );

export const useCostBreakdown = (p: CostQueryParams, groupBy: CostBreakdownGroupBy): CostQueryResult<CostBreakdown> =>
  useCostQuery(['breakdown', ...costKey(p), groupBy], () => analyticsV2Service.getCostBreakdown(p, groupBy), hasRange(p));

export const useCostFeatures = (p: CostQueryParams): CostQueryResult<CostFeatures> =>
  useCostQuery(['features', ...costKey(p)], () => analyticsV2Service.getCostFeatures(p), hasRange(p));

export const useCostWeekly = (p: CostQueryParams, level: CostWeeklyLevel): CostQueryResult<CostWeekly> =>
  useCostQuery(['weekly', ...costKey(p), level], () => analyticsV2Service.getCostWeekly(p, level), hasRange(p));

export const useCostTickets = (p: CostQueryParams, limit: number, offset: number): CostQueryResult<CostTickets> =>
  useCostQuery(['tickets', ...costKey(p), limit, offset], () => analyticsV2Service.getCostTickets(p, limit, offset), hasRange(p));

/** Filter options for the cost tab; keyed by range + brand only. */
export const useCostTicketTypes = (p: AnalyticsV2QueryParams): CostQueryResult<CostTicketTypes> =>
  useCostQuery(['ticket-types', ...rangeKey(p)], () => analyticsV2Service.getCostTicketTypes(p), hasRange(p));

/** Disabled while `conversationId` is null (no ticket picked). */
export const useCostTicket = (
  accountId: number,
  buId: string | null | undefined,
  conversationId: number | null,
): CostQueryResult<CostTicketDetail> =>
  useCostQuery(
    ['ticket', accountId, buId ?? 'all', conversationId],
    () => analyticsV2Service.getCostTicket({ accountId, buId, conversationId: conversationId! }),
    conversationId !== null,
  );


// ─── AI cost allowlist ─────────────────────────────────────────────────────────
// atomWithQuery (like use-feedbacks) so the mutations below refresh both reads by invalidating
// the shared key. No retry: a 403 is an answer, not a blip.

const COST_VIEWERS_KEY = ['analytics-v2', 'cost-viewers'] as const;

const costAccessQueryAtom = atomWithQuery(() => ({
  queryKey: [...COST_VIEWERS_KEY, 'me'],
  queryFn: () => analyticsV2Service.getCostAccess(),
  retry: false,
}));

const costViewersQueryAtom = atomWithQuery(() => ({
  queryKey: [...COST_VIEWERS_KEY, 'list'],
  queryFn: (): Promise<CostViewerList> => analyticsV2Service.listCostViewers(),
  retry: false,
}));

/** True only once the backend has said so; false while loading and on error. */
export const useCanViewCost = (): boolean => {
  const [query] = useAtom(costAccessQueryAtom);
  return query.data?.canView === true;
};

/** True until the backend answers whether this user can view cost. */
export const useCostAccessPending = (): boolean => useAtom(costAccessQueryAtom)[0].isPending;

export const useCostViewers = () => useAtom(costViewersQueryAtom)[0];

const refreshCostViewers = () => queryClient.invalidateQueries({ queryKey: COST_VIEWERS_KEY });

const COST_PRICES_KEY = ['analytics-v2', 'cost-prices'] as const;

const costPricesQueryAtom = atomWithQuery(() => ({
  queryKey: COST_PRICES_KEY,
  queryFn: (): Promise<UsagePriceList> => analyticsV2Service.listCostPrices(),
  retry: false,
}));

export const useCostPrices = () => useAtom(costPricesQueryAtom)[0];

export const saveCostPrice = async (input: UsagePriceInput) => {
  const saved = await analyticsV2Service.saveCostPrice(input);
  await queryClient.invalidateQueries({ queryKey: COST_PRICES_KEY });
  return saved;
};

export const useCostViewerMutations = () => ({
  add: async (email: string) => {
    await analyticsV2Service.addCostViewer(email);
    await refreshCostViewers();
  },
  remove: async (email: string) => {
    await analyticsV2Service.removeCostViewer(email);
    await refreshCostViewers();
  },
});


// ─── Sentinel Analytics ────────────────────────────────────────────────────────
// The same /metrics, /metrics/series and /metrics/breakdown reads, with Sentinel's keys and filters.

const sentinelKey = (q: SentinelAnalyticsQuery) => ['sentinel', q.accountId, q.fromDate, q.toDate, q.buId ?? 'all', q.filters ?? {}];

/** The cards' report for one account, range and filter: cards, daily series and failure reasons, fetched together. */
export function useSentinelAnalyticsReport(query: SentinelAnalyticsQuery | null) {
  return useAnalyticsQuery<SentinelAnalyticsReport>(query && [...sentinelKey(query), 'report'], async () => {
    const [metrics, series, failureReasons] = await Promise.all([
      analyticsV2Service.getMetrics(SENTINEL_METRIC_KEYS, query!),
      // One bucket per UTC day, the grain kim-ai-frontend's trend charts were drawn at.
      analyticsV2Service.getSeries(SENTINEL_SERIES_KEYS, { ...query!, granularity: 'day' }),
      analyticsV2Service.getBreakdown(['sentinelAiGenerationFailure'], ['failureReason'], query!),
    ]);
    return toSentinelReport(metrics, series, failureReasons, query!.fromDate, query!.toDate);
  });
}

const sentinelBreakdowns = (query: SentinelAnalyticsQuery, groupBys: SentinelDimension[][], keys: readonly SentinelMetricKey[]) => ({
  queryKey: [...sentinelKey(query), 'breakdowns', groupBys, keys],
  queryFn: (): Promise<SentinelBreakdownRow[][]> =>
    Promise.all(groupBys.map((groupBy) => analyticsV2Service.getBreakdown(keys, groupBy, query))),
});

/** One breakdown per entry of `groupBys`, fetched together, e.g. a table's category rows and their sub-category rows. */
export function useSentinelBreakdowns(
  query: SentinelAnalyticsQuery | null,
  groupBys: SentinelDimension[][],
  keys: readonly SentinelMetricKey[] = SENTINEL_METRIC_KEYS,
) {
  const read = query && sentinelBreakdowns(query, groupBys, keys);
  return useAnalyticsQuery(read && read.queryKey, () => read!.queryFn());
}

/** Loads a breakdown into the cache ahead of use, e.g. the other tab's, so switching to it is instant. */
export function prefetchSentinelBreakdowns(query: SentinelAnalyticsQuery, groupBys: SentinelDimension[][], keys: readonly SentinelMetricKey[]) {
  const { queryKey, queryFn } = sentinelBreakdowns(query, groupBys, keys);
  void queryClient.prefetchQuery({ queryKey: analyticsKey(queryKey), queryFn, staleTime: METRIC_STALE_TIME });
}
